export const PAYMENT_SOURCE_FINGERPRINT_VERSION = 1;
const instantText = (value) => value === null ? null : value.toISOString();
/**
 * The canonical JSON of fingerprint version 1: the keys in this order, `null` for an absent instant, instants as ISO 8601 UTC with
 * milliseconds, the amount as a plain integer (no float: a non-integer amount throws).
 */
export function canonicalPaymentSourceJson(input) {
    if (!Number.isSafeInteger(input.amountMinor) || input.amountMinor < 0) {
        throw new RangeError("the amount of a payment source is a non-negative integer number of paise");
    }
    if (!Number.isSafeInteger(input.sourceVersion) || input.sourceVersion < 1) {
        throw new RangeError("the version of a payment source is a positive integer");
    }
    return JSON.stringify({
        v: PAYMENT_SOURCE_FINGERPRINT_VERSION,
        kind: input.kind,
        sourceId: input.sourceId,
        sourceVersion: input.sourceVersion,
        priceAuthorizedAt: instantText(input.priceAuthorizedAt),
        validUntil: instantText(input.validUntil),
        amountMinor: input.amountMinor,
        currency: input.currency,
    });
}
/** `sha256_hex(canonical_json(v1))`. */
export function paymentSourceFingerprint(input) {
    return sha256Hex(canonicalPaymentSourceJson(input));
}
export const PAYMENT_SOURCE_FINGERPRINT_PATTERN = /^[0-9a-f]{64}$/;
// --- SHA-256 (FIPS 180-4), for UTF-8 text ---------------------------------------------------------------------------------------
const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
const rotr = (x, n) => (x >>> n) | (x << (32 - n));
export function sha256Hex(text) {
    const message = new TextEncoder().encode(text);
    const bitLength = message.length * 8;
    // The message, a 1 bit, zeros, and the 64-bit length: a multiple of 64 bytes.
    const paddedLength = (((message.length + 9 + 63) >> 6) << 6) >>> 0;
    const data = new Uint8Array(paddedLength);
    data.set(message);
    data[message.length] = 0x80;
    const view = new DataView(data.buffer);
    view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x1_0000_0000), false);
    view.setUint32(paddedLength - 4, bitLength >>> 0, false);
    const h = new Uint32Array([
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
    ]);
    const w = new Uint32Array(64);
    for (let offset = 0; offset < paddedLength; offset += 64) {
        for (let i = 0; i < 16; i += 1)
            w[i] = view.getUint32(offset + i * 4, false);
        for (let i = 16; i < 64; i += 1) {
            const w15 = w[i - 15];
            const w2 = w[i - 2];
            const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3);
            const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
        }
        let [a, b, c, d, e, f, g, hh] = h;
        for (let i = 0; i < 64; i += 1) {
            const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
            const choice = (e & f) ^ (~e & g);
            const t1 = (hh + s1 + choice + K[i] + w[i]) >>> 0;
            const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
            const majority = (a & b) ^ (a & c) ^ (b & c);
            const t2 = (s0 + majority) >>> 0;
            hh = g;
            g = f;
            f = e;
            e = (d + t1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (t1 + t2) >>> 0;
        }
        h[0] = (h[0] + a) >>> 0;
        h[1] = (h[1] + b) >>> 0;
        h[2] = (h[2] + c) >>> 0;
        h[3] = (h[3] + d) >>> 0;
        h[4] = (h[4] + e) >>> 0;
        h[5] = (h[5] + f) >>> 0;
        h[6] = (h[6] + g) >>> 0;
        h[7] = (h[7] + hh) >>> 0;
    }
    return Array.from(h, (word) => word.toString(16).padStart(8, "0")).join("");
}
